/* eslint-disable react/jsx-props-no-spreading */
import { useMemo } from 'react';
import {
  TextAction,
  UtilityText,
  styled,
  css,
  HeadingText,
} from '@leagueplatform/genesis-core';
import { type BoxProps } from '@leagueplatform/genesis-commons';
import { isAbsoluteUrl } from '@leagueplatform/web-common';
import { LinkLegacy as Link, NavLink } from '@leagueplatform/routing';
import { useIntl } from '@leagueplatform/locales';
import {
  type NavLink as NavLinkType,
  type NavItem,
} from 'hooks/use-nav-links-list.hook';
import {
  isHashLink,
  AppHashLink,
} from 'components/header-nav/app-hash-link.component';

type MobileNavLandingPageLinkProps = {
  messageId: string;
  to?: NavLinkType['to'];
  onClick?: () => void;
};

type MobileNavLinkProps = {
  text: string;
  to: NavItem['to'];
  description?: string;
  linkProps?: BoxProps;
  onClick?: (event: MouseEvent) => void;
  closeMenu?: () => void;
};

const getGenesisCoreColorToken = (genesisCommonColorToken: string) =>
  ({
    'onSurface.text.critical': '$onSurfaceTextCritical',
  })[genesisCommonColorToken];

const mobileNavLinkCss = css({
  textDecoration: 'none',
  borderRadius: '$small',
  '&&:hover': { color: '$interactiveActionPrimary' },
  '&&': { color: '$onSurfaceTextPrimary' },
});

export const StyledMobileNavLink = styled(Link, mobileNavLinkCss);

export const MobileNavLandingPageLink = ({
  messageId,
  to,
  onClick,
}: MobileNavLandingPageLinkProps) => {
  const { formatMessage } = useIntl();
  const linkProps = to ? { as: StyledMobileNavLink, to, onClick } : {};

  return (
    <HeadingText size="md" level="4" {...linkProps}>
      {formatMessage({ id: messageId })}
    </HeadingText>
  );
};

export const MobileNavLink = ({
  text,
  to,
  onClick,
  description,
  linkProps,
  closeMenu,
}: MobileNavLinkProps) => {
  const isExternalLink = useMemo(() => isAbsoluteUrl(to as string), [to]);
  const renderHashLink = useMemo(() => isHashLink(to), [to]);

  const as = useMemo(() => {
    if (isExternalLink) {
      return 'a';
    }
    if (renderHashLink) {
      return AppHashLink;
    }
    return to ? NavLink : 'button';
  }, [isExternalLink, to, renderHashLink]);

  return (
    <>
      <TextAction
        key={text}
        as={as}
        {...(to && !isExternalLink && { to })}
        onClick={(e: MouseEvent) => {
          if (onClick) {
            onClick(e);
          }
          closeMenu?.();
        }}
        {...(isExternalLink && {
          href: to,
          indicator: 'externalLink',
          target: '_blank',
          rel: 'noopener noreferrer',
        })}
        css={{
          color:
            getGenesisCoreColorToken(linkProps?.color as string) ||
            '$onSurfaceTextPrimary',
          fontWeight: '500',
          textAlign: 'left',
          '&:hover': {
            textDecoration: 'none',
            color: '$interactiveActionPrimary',
          },
        }}
      >
        {text}
      </TextAction>
      {description && (
        <UtilityText css={{ display: 'block', color: '$onSurfaceTextSubdued' }}>
          {description}
        </UtilityText>
      )}
    </>
  );
};
